import {getTestProps} from '@benchling/js-test-utils';
import {noop} from '@benchling/js-utils';
import {createStyles, Flex, Radio} from '@mantine/core';
import React, {ReactNode} from 'react';

import {noSpacingBottom, spacingBottom} from 'styles/Utilities';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import Label from 'ui-library/bs/Label';

export type RadioButtonOptionType = string | boolean | number | symbol;

export interface RadioButtonSelectorProps<TValue extends RadioButtonOptionType> {
  disabled?: boolean;
  hasBottomSpacing?: boolean;
  // May be null/undefined, in which case none of the radio buttons is selected.
  selectedValue?: TValue | null;
  options: Array<{
    /** A label to be rendered directly next to the radio button. */
    label: string | JSX.Element;
    /** A secondary, faded label with supporting details for the option. */
    secondaryLabel?: ReactNode;
    value: TValue;
    dataTestAttrs?: DataTestAttrs;
  }>;
  onChange: (value: TValue) => void;
  // Optional: useful for implementing toggle-off behavior (unselecting a radio button)
  onClick?: (value: TValue) => void;
  isVertical?: boolean | null;
  /** Optional: A class name to be applied to the label of the radio button. */
  labelClassName?: string;
  /** @default wrap */
  wrap?: 'wrap' | 'nowrap';
}

export interface RadioButtonGroupProps<T extends RadioButtonOptionType> extends RadioButtonSelectorProps<T> {
  title: string;
  hasBottomSpacing?: boolean;
}

const useStyles = createStyles(() => ({
  buttonGroupBottomSpacing: spacingBottom,
  buttonGroupNoBottomSpacing: noSpacingBottom,
}));

export function RadioButtonSelector<TValue extends RadioButtonOptionType>({
  disabled = false,
  wrap = 'wrap',
  ...otherProps
}: RadioButtonSelectorProps<TValue>): JSX.Element {
  return (
    <Flex gap="4px 20px" direction={otherProps.isVertical ? 'column' : 'row'} wrap={wrap}>
      {otherProps.options.map((option) => (
        <Radio
          key={String(option.value)}
          classNames={{label: otherProps.labelClassName}}
          checked={otherProps.selectedValue === option.value}
          onChange={() => {
            otherProps.onChange(option.value);
          }}
          onClick={
            otherProps.onClick != null
              ? () => {
                  otherProps.onClick!(option.value);
                }
              : noop
          }
          disabled={disabled}
          value={String(option.value)}
          label={option.label}
          description={
            option.secondaryLabel && (
              <div
                // For some reason, Mantine's Radio doesn't trigger onChange when clicking on the description,
                // even though it has a pointer cursor.
                onClick={() => {
                  otherProps.onChange(option.value);
                }}
              >
                {option.secondaryLabel}
              </div>
            )
          }
          // On wrapper to avoid having to change lots of tests.
          wrapperProps={getTestProps(option.dataTestAttrs)}
        />
      ))}
    </Flex>
  );
}

export default function RadioButtonGroup<TValue extends RadioButtonOptionType>({
  title,
  hasBottomSpacing = true,
  ...remainingProps
}: RadioButtonGroupProps<TValue>): JSX.Element {
  const {classes} = useStyles();
  return (
    <div
      className={hasBottomSpacing ? classes.buttonGroupBottomSpacing : classes.buttonGroupNoBottomSpacing}
      role="radiogroup"
    >
      <Label className="u-display-block">{title}</Label>
      <RadioButtonSelector {...remainingProps} />
    </div>
  );
}
